import {
  FC,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import { getCatalogs } from '../services/apiCalls';
import { toast } from 'react-toastify';
import { useInfiniteScroll } from '../hooks/useInfiniteScrolling';
import {
  CatalogFilterType,
  ISourceDocument,
  ITargetDocument,
  SourceDocumentType,
  SourceDocumentTypeEnum,
  FormattedOptions,
  SourceModalFilterType,
  AutomappingScreenFilterType,
  DistinctFilterResponse,
} from '../types';
import { CatalogContext } from '../contexts/CatalogContext';
import { Table } from './table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { CatalogSelectField } from './elements/CatalogSelectField';
import { updateParams } from '../helpers/paramsHelpers';
import SvgIcon from './elements/SvgIcon';
import { TableHeader } from '@GxT-Team/gxt-components';
import { archiveCatalog } from '../services/api';

export interface ISourceDocuments {
  activeTab?: string;
  filters: CatalogFilterType | SourceModalFilterType | AutomappingScreenFilterType;
  setFilters: (key: keyof (CatalogFilterType | SourceModalFilterType), value: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  selectedSourceDocuments?: number[];
  setSelectedSourceDocuments?: (id: number[]) => void;
  selectedTargetId?: number | null;
  setSelectedTarget?: (target: number | null) => void;
  refreshFiles?: boolean;
  setRefreshFiles?: (refreshFiles: boolean) => void;
  actionsColumn?: boolean;
  originPath?: string;
  page?: boolean;
  selectAllOption?: boolean;
  setDropdownValues?: Dispatch<SetStateAction<FormattedOptions>>;
  getDistinctProgramFilters?: (response: DistinctFilterResponse) => void;
  setPreviewDocId?: (id: number) => void;
  isFileInterective?: (isLoading: boolean) => void;
  openPreviewModal?: () => void;
}

const RECORDS_PER_PAGE = 100;
const columnHelper = createColumnHelper<ISourceDocument>();

export const SourceDocuments: FC<ISourceDocuments> = ({
  filters,
  setIsLoading,
  selectedSourceDocuments,
  setSelectedSourceDocuments,
  selectedTargetId,
  setSelectedTarget,
  refreshFiles = false,
  setRefreshFiles,
  actionsColumn = true,
  // setFilters,
  activeTab,
  originPath,
  page,
  selectAllOption,
  getDistinctProgramFilters,
  setPreviewDocId,
  isFileInterective,
  openPreviewModal,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { setSourceFiles } = useContext(CatalogContext);

  const handleSelectedFiles = useCallback(
    (doc: ITargetDocument, type?: SourceDocumentType) => {
      const { id } = doc;
      if (
        type === SourceDocumentTypeEnum.Source &&
        selectedSourceDocuments &&
        setSelectedSourceDocuments
      ) {
        if (selectedSourceDocuments.includes(id)) {
          setSelectedSourceDocuments(selectedSourceDocuments.filter((file) => file !== id));
        } else {
          setSelectedSourceDocuments([...selectedSourceDocuments, id]);
        }
      } else if (setSelectedTarget) {
        setSelectedTarget(id);
      }
    },
    [selectedSourceDocuments, setSelectedSourceDocuments, setSelectedTarget],
  );

  const { data, refreshData, isDataLoading, response } = useInfiniteScroll<ISourceDocument>(
    { type: '', statusType: '' },
    containerRef,
    getCatalogs,
    RECORDS_PER_PAGE,
    {
      ...filters,
      // ...(filters.columnSort && { columnSort: getColumnName(filters.columnSort) }),
    },
    setSourceFiles,
  );

  const deleteSource = useCallback(
    (id: number) => {
      archiveCatalog([id])
        .then((res) => {
          toast.success(res.data.message);
          refreshData();
        })
        .catch((res) => {
          toast.error(res.data.message);
        });
    },
    [refreshData],
  );

  useEffect(() => {
    if (response) {
      getDistinctProgramFilters?.(response);
    }
  }, [response, getDistinctProgramFilters]);

  useEffect(() => {
    if (page && originPath) {
      updateParams({ basePath: originPath, filters, activeTab });
    }
  }, [filters, activeTab, originPath, page]);

  useEffect(() => {
    setIsLoading(isDataLoading);
  }, [isDataLoading, setIsLoading]);

  const handleColumnSort = useCallback((_sort: string, _column: string) => {
    // setFilters('columnSort', column);
    // setFilters('sortType', sort);
  }, []);

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const catalogIds = data.map((catalog: ISourceDocument) => catalog.id);
      const selectedSourceIds: number[] = event.target.checked
        ? [...new Set([...(selectedSourceDocuments || []), ...catalogIds])]
        : (selectedSourceDocuments &&
            selectedSourceDocuments.filter((id) => !catalogIds.includes(id))) ||
          [];

      setSelectedSourceDocuments?.(selectedSourceIds);
    },
    [data, selectedSourceDocuments, setSelectedSourceDocuments],
  );

  const allDocsChecked = useMemo(() => {
    if (data.length === 0) return false;
    return data
      .map((item: ISourceDocument) => item.id)
      .every((id: number) => selectedSourceDocuments?.includes(id));
  }, [data, selectedSourceDocuments]);

  const handlePreviewDocument = useCallback(
    (id: number, documentType: boolean) => {
      setPreviewDocId?.(id);
      openPreviewModal?.();
      isFileInterective?.(documentType);
    },
    [setPreviewDocId, openPreviewModal, isFileInterective],
  );

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('id', {
        header: () =>
          selectAllOption ? (
            <input type="checkbox" checked={allDocsChecked} onChange={handleSelectAll} />
          ) : (
            ''
          ),
        cell: ({ row }) => (
          <CatalogSelectField
            type="checkbox"
            record={row}
            handleChange={handleSelectedFiles}
            checked={[selectedTargetId, ...(selectedSourceDocuments || [])].includes(
              row.original.id,
            )}
          />
        ),
        size: 10,
        maxSize: 10,
      }),
      columnHelper.accessor('id', {
        header: () => (
          <TableHeader ColumnName="id" Title="ID" handleColumnSort={handleColumnSort} />
        ),
        cell: (info) => info.getValue(),
        size: 60,
      }),
      columnHelper.accessor('documentName', {
        header: () => (
          <TableHeader
            ColumnName="documentName"
            Title="Document Name"
            handleColumnSort={handleColumnSort}
          />
        ),
        cell: (info) => info.getValue(),
        size: 700,
      }),
      columnHelper.accessor('program', {
        header: () => (
          <TableHeader ColumnName="program" Title="Program" handleColumnSort={handleColumnSort} />
        ),
        cell: (info) => info.getValue(),
        size: 30,
      }),
      columnHelper.accessor('version', {
        header: () => (
          <TableHeader ColumnName="version" Title="Version" handleColumnSort={handleColumnSort} />
        ),
        cell: (info) => info.getValue(),
        size: 30,
      }),
      columnHelper.accessor('documentCat', {
        header: () => (
          <TableHeader
            ColumnName="documentCat"
            Title="Category"
            handleColumnSort={handleColumnSort}
          />
        ),
        cell: (info) => info.renderValue(),
      }),
      columnHelper.accessor('documentAuthor', {
        header: () => (
          <TableHeader
            ColumnName="firstName"
            Title="Uploaded By"
            handleColumnSort={handleColumnSort}
          />
        ),
        cell: (info) => {
          const createdBy = info.row.original.createdBy;
          const fullName = createdBy ? `${createdBy.firstName} ${createdBy.lastName}` : '';
          return fullName;
        },
      }),
    ];

    if (actionsColumn) {
      return [
        ...baseColumns,
        columnHelper.display({
          id: 'actions',
          header: () => 'Actions',
          cell: ({ row }) => (
            <div className="space-x-2">
              {setPreviewDocId && (
                <button
                  data-testid="preview-document-btn"
                  onClick={() => {
                    handlePreviewDocument(
                      row.original.isInteractive
                        ? row.original.id
                        : row.original.nonParsedCatalogId,
                      row.original.isInteractive,
                    );
                  }}>
                  <SvgIcon iconType="previewDocument" />
                </button>
              )}
              <DeleteConfirmationModal deleteHandler={deleteSource} id={row.original.id} />
            </div>
          ),
          size: 10,
          maxSize: 10,
        }),
      ];
    }

    return baseColumns;
  }, [
    selectAllOption,
    allDocsChecked,
    handleSelectAll,
    handleSelectedFiles,
    selectedTargetId,
    selectedSourceDocuments,
    handleColumnSort,
    actionsColumn,
    setPreviewDocId,
    handlePreviewDocument,
    deleteSource,
  ]);

  useEffect(() => {
    if (refreshFiles) {
      refreshData();
      setRefreshFiles?.(false);
    }
  }, [refreshFiles, refreshData, setRefreshFiles]);

  return (
    <div
      className="mt-0 overflow-y-auto overflow-hidden my-4"
      style={{ height: '60vh' }}
      ref={containerRef}>
      <Table data={data} columns={columns} />
    </div>
  );
};
